import { motion, useReducedMotion } from "framer-motion";
import { revealVariants, staticVariants, groupVariants, VIEWPORT, DUR, EASE_OUT } from "../lib/motion";

/**
 * Scroll reveal: a short fade with a 18px lift, fired once per element.
 *
 * Under `prefers-reduced-motion` the final state renders immediately, so
 * content is never invisible-by-default for those users.
 */
export function Reveal({ as = "div", delay = 0, children, ...rest }) {
  const prefersReducedMotion = useReducedMotion();
  const Tag = motion[as] || motion.div;

  if (prefersReducedMotion) {
    const Plain = as;
    return <Plain {...rest}>{children}</Plain>;
  }

  return (
    <Tag
      initial="hidden"
      whileInView="shown"
      viewport={VIEWPORT}
      variants={revealVariants}
      transition={{ duration: DUR.reveal, ease: EASE_OUT, delay }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Parent for a staggered group. Children must be `RevealItem`s.
 * Stagger is kept at 42ms so even an eight-item grid finishes quickly.
 */
export function RevealGroup({ as = "div", children, ...rest }) {
  const prefersReducedMotion = useReducedMotion();
  const Tag = motion[as] || motion.div;

  return (
    <Tag
      initial="hidden"
      whileInView="shown"
      viewport={VIEWPORT}
      variants={prefersReducedMotion ? staticVariants : groupVariants}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function RevealItem({ as = "div", children, ...rest }) {
  const prefersReducedMotion = useReducedMotion();
  const Tag = motion[as] || motion.div;

  return (
    <Tag variants={prefersReducedMotion ? staticVariants : revealVariants} {...rest}>
      {children}
    </Tag>
  );
}

export default Reveal;
